import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField, FormControl, FormHelperText, CircularProgress,
    Box, Typography, Grid, Button, IconButton, ToggleButtonGroup,
    ToggleButton, Paper, Snackbar, Alert, useMediaQuery, useTheme,
    AppBar, Toolbar, Chip, Card, CardContent, Divider, Popover,
    Select, MenuItem, InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    useSalonsCreateMutation,
    useSalonsPartialUpdateMutation,
    useAuthProfileRetrieveQuery
} from '../../../services/GLOWClient';
import theme from '../../../theme';
import { superadminService } from '../../../services/superadmin.service';
import FullScreenDialog from '../../../SystemDesign/FullScreenDialog';
import SalonAddressPicker from './SalonAddressPicker';
import CreationEditView from './creationEditView';
import TranslationValidationModal from './TranslationValidationModal';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { getLocalizedField } from '../../../utils/localizedFields';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import TranslateIcon from '@mui/icons-material/Translate';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import SalonProposalExpiryField from './SalonProposalExpiryField';
import { focusSalonPhotoSection } from './salonPhotoEditQuery';

const getExistingSalonLocation = (salon) => {
    if (!salon) return { locationData: null, locationPreview: null };

    const rawLocation = Array.isArray(salon.location_data)
        ? salon.location_data[0]
        : salon.location || salon.location_data;
    const latitude = rawLocation?.latitude ?? salon.latitude;
    const longitude = rawLocation?.longitude ?? salon.longitude;
    const formattedAddress = rawLocation?.formatted_address || salon.formatted_address || '';
    const hasCoordinates = (
        latitude !== null
        && latitude !== undefined
        && latitude !== ''
        && longitude !== null
        && longitude !== undefined
        && longitude !== ''
    );

    if (!formattedAddress && !hasCoordinates) {
        return { locationData: null, locationPreview: null };
    }

    const candidate = {
        formatted_address: formattedAddress,
        latitude: hasCoordinates ? Number(latitude) : null,
        longitude: hasCoordinates ? Number(longitude) : null,
        country_code: rawLocation?.country_code || '',
        country_name: rawLocation?.country_name || '',
        admin_area_1: rawLocation?.admin_area_1 || '',
        admin_area_2: rawLocation?.admin_area_2 || '',
        locality: rawLocation?.locality || '',
        postal_code: rawLocation?.postal_code || '',
        provider: rawLocation?.provider || rawLocation?.geocoding_provider || '',
        provider_place_id: rawLocation?.provider_place_id || '',
        raw: rawLocation?.raw || {},
    };
    const isStructured = Boolean(
        candidate.formatted_address
        && candidate.country_code
        && candidate.admin_area_1
        && candidate.locality
        && Number.isFinite(candidate.latitude)
        && Number.isFinite(candidate.longitude)
    );

    return {
        locationData: isStructured ? candidate : null,
        locationPreview: candidate,
    };
};

const AddSalon = ({
    open,
    onClose,
    onSuccess,
    isUpdateMode = false,
    salonToUpdate = null,
    focusPhotos = false,
    onPhotoFocusConsumed,
}) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const photoSectionRef = useRef(null);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
    const { t, i18n } = useTranslation();

    // Set dayjs locale based on current language
    useEffect(() => {
        const localeMap = {
            'fr': 'fr',
            'ar': 'ar',
            'en': 'en'
        };
        dayjs.locale(localeMap[i18n.language] || 'en');
    }, [i18n.language]);

    const handlePhotoDialogEntered = useCallback(() => {
        if (focusPhotos) {
            focusSalonPhotoSection(photoSectionRef.current, onPhotoFocusConsumed);
        }
    }, [focusPhotos, onPhotoFocusConsumed]);

    const getLocalizedName = (salon) => {
        return getLocalizedField(salon, 'name', i18n.language);
    };

    const getLocalizedDescription = (salon) => {
        return getLocalizedField(salon, 'description', i18n.language);
    };

    // State to track if salon is created and show management section
    const [salonCreated, setSalonCreated] = useState(false);
    const [createdSalonData, setCreatedSalonData] = useState(null);
    const [showEditForm, setShowEditForm] = useState(false);

    // State to track photos that need to be deleted
    const [photosToDelete, setPhotosToDelete] = useState([]);

    // Track existing photos count for proper indexing
    const [existingPhotosCount, setExistingPhotosCount] = useState(0);
    // Track existing photo IDs aligned with previews order
    const [existingPhotoIds, setExistingPhotoIds] = useState([]);

    // Days of the week - memoized to prevent recreation
    const weekdays = useMemo(() => [
        { id: 'lun', name: t('pro_dashboard.week_days.abbrev.monday'), fullName: t('pro_dashboard.week_days.monday') },
        { id: 'mar', name: t('pro_dashboard.week_days.abbrev.tuesday'), fullName: t('pro_dashboard.week_days.tuesday') },
        { id: 'mer', name: t('pro_dashboard.week_days.abbrev.wednesday'), fullName: t('pro_dashboard.week_days.wednesday') },
        { id: 'jeu', name: t('pro_dashboard.week_days.abbrev.thursday'), fullName: t('pro_dashboard.week_days.thursday') },
        { id: 'ven', name: t('pro_dashboard.week_days.abbrev.friday'), fullName: t('pro_dashboard.week_days.friday') },
        { id: 'sam', name: t('pro_dashboard.week_days.abbrev.saturday'), fullName: t('pro_dashboard.week_days.saturday') },
        { id: 'dim', name: t('pro_dashboard.week_days.abbrev.sunday'), fullName: t('pro_dashboard.week_days.sunday') },
    ], [t, i18n.language]);

    // Default opening hours - memoized to prevent recreation
    const defaultOpeningHours = useMemo(() => ({
        lundi: { ouverture: '09:00', fermeture: '18:00' },
        mardi: { ouverture: '09:00', fermeture: '18:00' },
        mercredi: { ouverture: '09:00', fermeture: '18:00' },
        jeudi: { ouverture: '09:00', fermeture: '18:00' },
        vendredi: { ouverture: '09:00', fermeture: '18:00' },
        samedi: { ouverture: '10:00', fermeture: '17:00' },
        dimanche: { ouverture: t('pro_dashboard.salon.closed'), fermeture: t('pro_dashboard.salon.closed') }
    }), [t, i18n.language]);

    // Initialize form data function - memoized with useCallback
    const initializeFormData = useCallback(() => {
        if (isUpdateMode && salonToUpdate) {
            const { locationData, locationPreview } = getExistingSalonLocation(salonToUpdate);
            return {
                name: salonToUpdate.name || '',
                description: salonToUpdate.description || '',
                address: salonToUpdate.formatted_address || '',
                phone: salonToUpdate.phone || '',
                email: salonToUpdate.email || '',
                opening_hours: salonToUpdate.opening_hours || defaultOpeningHours,
                appointment_proposal_default_expiry_hours: salonToUpdate.appointment_proposal_default_expiry_hours || 24,
                photo_caption: '',
                latitude: salonToUpdate.latitude || '',
                longitude: salonToUpdate.longitude || '',
                formatted_address: salonToUpdate.formatted_address || '',
                location_data: locationData,
                location_preview: locationPreview
            };
        } else {
            return {
                name: '',
                description: '',
                address: '',
                phone: '',
                email: '',
                opening_hours: defaultOpeningHours,
                appointment_proposal_default_expiry_hours: 24,
                photo_caption: '',
                latitude: '',
                longitude: '',
                formatted_address: '',
                location_data: null,
                location_preview: null
            };
        }
    }, [isUpdateMode, salonToUpdate, defaultOpeningHours]);

    // Initialize selected days function - memoized with useCallback
    const initializeSelectedDays = useCallback(() => {
        if (isUpdateMode && salonToUpdate && salonToUpdate.opening_hours) {
            const days = [];
            if (salonToUpdate.opening_hours.lundi && salonToUpdate.opening_hours.lundi.ouverture !== t('pro_dashboard.salon.closed')) days.push('lun');
            if (salonToUpdate.opening_hours.mardi && salonToUpdate.opening_hours.mardi.ouverture !== t('pro_dashboard.salon.closed')) days.push('mar');
            if (salonToUpdate.opening_hours.mercredi && salonToUpdate.opening_hours.mercredi.ouverture !== t('pro_dashboard.salon.closed')) days.push('mer');
            if (salonToUpdate.opening_hours.jeudi && salonToUpdate.opening_hours.jeudi.ouverture !== t('pro_dashboard.salon.closed')) days.push('jeu');
            if (salonToUpdate.opening_hours.vendredi && salonToUpdate.opening_hours.vendredi.ouverture !== t('pro_dashboard.salon.closed')) days.push('ven');
            if (salonToUpdate.opening_hours.samedi && salonToUpdate.opening_hours.samedi.ouverture !== t('pro_dashboard.salon.closed')) days.push('sam');
            if (salonToUpdate.opening_hours.dimanche && salonToUpdate.opening_hours.dimanche.ouverture !== t('pro_dashboard.salon.closed')) days.push('dim');
            return days.length > 0 ? days : ['lun', 'mar', 'mer', 'jeu', 'ven'];
        }
        return ['lun', 'mar', 'mer', 'jeu', 'ven'];
    }, [isUpdateMode, salonToUpdate, t, i18n.language]);

    // State variables
    const [formData, setFormData] = useState(() => initializeFormData());
    const [locationTouched, setLocationTouched] = useState(false);
    const [selectedDays, setSelectedDays] = useState(() => initializeSelectedDays());
    const [photos, setPhotos] = useState([]); // Only for NEW photos
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photosPreviews, setPhotosPreviews] = useState([]); // All photos (existing + new)
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [photoCaptions, setPhotoCaptions] = useState([]); // All captions (existing + new)
    const [primaryPhotoIndex, setPrimaryPhotoIndex] = useState(0); // Explicit primary selection
    const [errors, setErrors] = useState({});
    const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);

    // SuperAdmin Owner Selection
    const [owners, setOwners] = useState([]);
    const [loadingOwners, setLoadingOwners] = useState(false);
    const [selectedOwnerId, setSelectedOwnerId] = useState('');

    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    const isSuperAdmin = userProfile?.role === 'superadmin' || userProfile?.role === 'admin';

    // DEBUG LOGS - TRACE RENDER STATE
    // console.log("[GLOW][AddSalon] RENDER STATE:", {
    //     isSuperAdmin,
    //     loadingOwners,
    //     ownersCount: owners.length,
    //     selectedOwnerId,
    //     open
    // });

    // Time picker popover states
    const [timePickerAnchor, setTimePickerAnchor] = useState(null);
    const [activeTimePicker, setActiveTimePicker] = useState(null); // { day, type }

    // States for Snackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const openingHoursErrorRef = useRef(null);
    const scrollToOpeningHoursError = useCallback(() => {
        const performScroll = () => {
            const errorElement = openingHoursErrorRef.current;

            if (errorElement) {
                errorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                const dialogContent = errorElement.closest('.MuiDialogContent-root');
                if (dialogContent?.scrollTo) {
                    dialogContent.scrollTo({ top: 0, behavior: 'smooth' });
                }

                const dialogPaper = errorElement.closest('.MuiDialog-paper');
                if (dialogPaper?.scrollTo) {
                    dialogPaper.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }

            if (typeof window !== 'undefined' && window?.scrollTo) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(performScroll);
        });
    }, []);

    useEffect(() => {
        if (!errors?.opening_hours) return;

        scrollToOpeningHoursError();
    }, [errors, scrollToOpeningHoursError]);

    // Fetch owners if superadmin
    useEffect(() => {
        let isMounted = true;
        const fetchOwners = async () => {
            if (isSuperAdmin && open) {
                // console.log("[GLOW][AddSalon] fetchOwners START", { isSuperAdmin, open });
                setLoadingOwners(true);
                try {
                    const res = await superadminService.listProfessionals();
                    // console.log("[GLOW][AddSalon] API Response RAW:", res);

                    if (!isMounted) return;

                    // Handle Axios response structure
                    const data = res.data;
                    const ownersData = Array.isArray(data) ? data : (data?.results || []);

                    setOwners(ownersData);
                    // console.log("[GLOW][AddSalon] Owners state updated:", ownersData.length);
                } catch (err) {
                    console.error("[GLOW][AddSalon] fetchOwners ERROR:", err);
                } finally {
                    if (isMounted) {
                        setLoadingOwners(false);
                        // console.log("[GLOW][AddSalon] setLoadingOwners(false) called");
                    }
                }
            }
        };
        fetchOwners();
        return () => { isMounted = false; };
    }, [isSuperAdmin, open]);

    // Load existing photos when opening in update mode
    useEffect(() => {
        if (isUpdateMode && salonToUpdate?.photos && open) {
            const existingPreviews = [];
            const existingCaptions = [];
            const existingIds = [];
            let photoCount = 0;
            let primaryIndex = 0;

            if (Array.isArray(salonToUpdate.photos)) {
                salonToUpdate.photos.forEach((photo, idx) => {
                    if (photo.url) {
                        existingPreviews.push(photo.url);
                        existingCaptions.push(photo.caption || '');
                        existingIds.push(photo.id || photo.photo_id || photo.pk || null);
                        if (photo.is_primary) primaryIndex = idx;
                        photoCount++;
                    }
                });
            } else if (typeof salonToUpdate.photos === 'object') {
                const entries = Object.entries(salonToUpdate.photos);
                entries.forEach(([key, photoUrl]) => {
                    if (photoUrl && typeof photoUrl === 'string') {
                        existingPreviews.push(photoUrl);
                        existingCaptions.push('');
                        existingIds.push(parseInt(key, 10) || key);
                        photoCount++;
                    }
                });
            }

            setPhotosPreviews(existingPreviews);
            setPhotoCaptions(existingCaptions);
            setExistingPhotosCount(photoCount);
            setExistingPhotoIds(existingIds);
            setPhotoPreview(existingPreviews.length > 0 ? existingPreviews[primaryIndex] : null);
            setCurrentPhotoIndex(primaryIndex);
            setPrimaryPhotoIndex(primaryIndex);
            setPhotos([]);
            setPhotosToDelete([]);
        }
    }, [isUpdateMode, salonToUpdate, open]);

    // Translation validation modal states
    const [translationModalOpen, setTranslationModalOpen] = useState(false);
    const [translationModalLoading, setTranslationModalLoading] = useState(false);
    const [pendingTranslations, setPendingTranslations] = useState(null);
    const [createdSalonId, setCreatedSalonId] = useState(null);
    const [pendingOnSuccess, setPendingOnSuccess] = useState(null);
    const pollIntervalRef = React.useRef(null);

    // Create and update salon mutations
    const [createSalon, { isLoading: isCreating }] = useSalonsCreateMutation();
    const [updateSalon, { isLoading: isUpdating }] = useSalonsPartialUpdateMutation();

    // Handle successful operations
    const handleSuccess = (message) => {
        console.log('AddSalon success snackbar:', message);
        setSnackbarMessage(message);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field if it exists
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleAddressCandidateChange = useCallback((candidate) => {
        setLocationTouched(true);
        setFormData(prev => {
            if (!candidate) {
                return {
                    ...prev,
                    location_data: null,
                    location_preview: null
                };
            }

            return {
                ...prev,
                address: candidate.formatted_address,
                formatted_address: candidate.formatted_address,
                latitude: candidate.latitude,
                longitude: candidate.longitude,
                location_data: candidate,
                location_preview: candidate
            };
        });
        setErrors(prev => ({ ...prev, location_data: null }));
    }, []);

    // Validate form fields before submission
    const validateForm = useCallback(() => {
        const newErrors = {};
        const phoneRegex = /^[+]?[0-9]{1,15}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.name?.trim()) {
            newErrors.name = t('pro_dashboard.salon.name_required');
        }

        if (isSuperAdmin && !selectedOwnerId) {
            newErrors.owner = t('pro_dashboard.salon.owner_required');
        }

        if (formData.phone?.trim() && !phoneRegex.test(formData.phone.trim())) {
            newErrors.phone = t('pro_dashboard.salon.phone_invalid');
        }

        if (formData.email?.trim() && !emailRegex.test(formData.email.trim())) {
            newErrors.email = t('pro_dashboard.salon.email_invalid');
        }

        if ((!isUpdateMode || locationTouched) && !formData.location_data) {
            newErrors.location_data = t('pro_dashboard.salon.address_confirmation_required');
        }

        return newErrors;
    }, [
        formData.name,
        formData.phone,
        formData.email,
        formData.location_data,
        isSuperAdmin,
        selectedOwnerId,
        isUpdateMode,
        locationTouched,
        t,
        i18n.language
    ]);

    // Handle opening hours changes
    const handleHoursChange = useCallback((day, type, value) => {
        const timeValue = value ? value.format('HH:mm') : t('pro_dashboard.salon.closed');
        setFormData(prevState => ({
            ...prevState,
            opening_hours: {
                ...prevState.opening_hours,
                [day]: {
                    ...prevState.opening_hours[day],
                    [type]: timeValue
                }
            }
        }));
        // Close the time picker popover
        setTimePickerAnchor(null);
        setActiveTimePicker(null);
    }, [t, i18n.language]);

    // Handle direct time input changes (typing) with auto-formatting
    const handleTimeInputChange = useCallback((day, type, value, event) => {
        if (!value || value === t('pro_dashboard.salon.closed')) {
            setFormData(prevState => ({
                ...prevState,
                opening_hours: {
                    ...prevState.opening_hours,
                    [day]: {
                        ...prevState.opening_hours[day],
                        [type]: t('pro_dashboard.salon.closed')
                    }
                }
            }));
            return;
        }

        // Remove non-numeric characters except colon
        let cleanValue = value.replace(/[^\d:]/g, '');

        // Get current value to detect if user is deleting
        const currentValue = formData.opening_hours[day]?.[type] || '';
        const isDeleting = cleanValue.length < currentValue.length;

        // Don't auto-format if user is deleting
        if (!isDeleting) {
            // Auto-format as user types
            if (cleanValue.length === 1 && parseInt(cleanValue) > 2) {
                cleanValue = '0' + cleanValue + ':';
            } else if (cleanValue.length === 2 && !cleanValue.includes(':')) {
                const hours = parseInt(cleanValue);
                if (hours > 23) {
                    cleanValue = '23:';
                } else {
                    cleanValue = cleanValue + ':';
                }
            } else if (cleanValue.length === 4 && cleanValue.includes(':')) {
                const [hours, minutes] = cleanValue.split(':');
                const minuteValue = parseInt(minutes);
                if (minuteValue > 5) {
                    cleanValue = hours + ':0' + minutes;
                }
            } else if (cleanValue.length === 5) {
                const [hours, minutes] = cleanValue.split(':');
                const hourValue = parseInt(hours);
                const minuteValue = parseInt(minutes);

                if (hourValue > 23) {
                    cleanValue = '23:' + minutes;
                }
                if (minuteValue > 59) {
                    cleanValue = hours + ':59';
                }
            }
        }

        // Validate final format - allow partial input
        const timeRegex = /^([01]?[0-9]|2[0-3]):?([0-5]?[0-9]?)$/;
        if (!timeRegex.test(cleanValue)) {
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            opening_hours: {
                ...prevState.opening_hours,
                [day]: {
                    ...prevState.opening_hours[day],
                    [type]: cleanValue
                }
            }
        }));
    }, [formData.opening_hours, t, i18n.language]);

    // Handle time input blur (when user leaves the field) - auto-complete
    const handleTimeInputBlur = useCallback((day, type) => {
        const currentValue = formData.opening_hours[day]?.[type];

        if (!currentValue || currentValue === t('pro_dashboard.salon.closed')) {
            return;
        }

        let cleanValue = currentValue;

        // Auto-complete incomplete time values on blur
        if (cleanValue.includes(':')) {
            const parts = cleanValue.split(':');
            if (parts.length === 2) {
                const hours = parts[0].padStart(2, '0');
                const minutes = parts[1] ? parts[1].padStart(2, '0') : '00';
                cleanValue = `${hours}:${minutes}`;
            }
        } else if (cleanValue.length > 0 && cleanValue.length <= 2) {
            // If only hours are provided, add :00
            cleanValue = cleanValue.padStart(2, '0') + ':00';
        }

        // Only update if the value changed
        if (cleanValue !== currentValue) {
            setFormData(prevState => ({
                ...prevState,
                opening_hours: {
                    ...prevState.opening_hours,
                    [day]: {
                        ...prevState.opening_hours[day],
                        [type]: cleanValue
                    }
                }
            }));
        }
    }, [formData.opening_hours, t, i18n.language]);

    // Handle time picker open
    const handleTimePickerOpen = useCallback((event, day, type) => {
        setTimePickerAnchor(event.currentTarget);
        setActiveTimePicker({ day, type });
    }, []);

    // Handle time picker close
    const handleTimePickerClose = useCallback(() => {
        setTimePickerAnchor(null);
        setActiveTimePicker(null);
    }, []);

    // Handle day selection
    const handleDayChange = useCallback((event, newDays) => {
        if (newDays && newDays.length) {
            setSelectedDays(newDays);
            setFormData(prev => {
                const updatedOpeningHours = { ...prev.opening_hours };
                weekdays.forEach(day => {
                    if (!updatedOpeningHours[day.fullName]) {
                        updatedOpeningHours[day.fullName] = { ouverture: t('pro_dashboard.salon.closed'), fermeture: t('pro_dashboard.salon.closed') };
                    }
                    const isSelected = newDays.includes(day.id);
                    if (!isSelected && updatedOpeningHours[day.fullName].ouverture !== t('pro_dashboard.salon.closed')) {
                        updatedOpeningHours[day.fullName] = { ouverture: t('pro_dashboard.salon.closed'), fermeture: t('pro_dashboard.salon.closed') };
                    }
                });
                return { ...prev, opening_hours: updatedOpeningHours };
            });
        }
    }, [weekdays, t, i18n.language]);

    // Handle photo upload with immediate preview
    const handlePhotoUpload = useCallback((e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setPhotos(prevPhotos => [...prevPhotos, ...files]);
            setPhotoCaptions(prevCaptions => [...prevCaptions, ...Array(files.length).fill('')]);
            Promise.all(files.map(file => new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(file);
            }))).then(newPreviews => {
                setPhotosPreviews(prevPreviews => {
                    const updatedPreviews = [...prevPreviews, ...newPreviews];
                    const newIndex = prevPreviews.length;
                    setCurrentPhotoIndex(newIndex);
                    setPhotoPreview(updatedPreviews[newIndex]);
                    if (prevPreviews.length === 0) {
                        setPrimaryPhotoIndex(newIndex);
                    }
                    return updatedPreviews;
                });
            });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    // Handle selecting a photo as current
    const handleSelectPhoto = useCallback((index) => {
        if (index >= 0 && index < photosPreviews.length) {
            setCurrentPhotoIndex(index);
            setPhotoPreview(photosPreviews[index]);
        }
    }, [photosPreviews]);

    // Handle removing a photo
    const handleRemovePhoto = useCallback((index) => {
        if (index < 0 || index >= photosPreviews.length) return;

        if (isUpdateMode && index < existingPhotosCount) {
            const existingId = existingPhotoIds[index];
            if (existingId !== undefined && existingId !== null) {
                setPhotosToDelete(prev => [...prev, existingId]);
            }
            setExistingPhotoIds(prev => prev.filter((_, i) => i !== index));
            setExistingPhotosCount(prev => Math.max(0, prev - 1));
        } else {
            const newPhotoIndex = index - existingPhotosCount;
            setPhotos(prevPhotos => prevPhotos.filter((_, i) => i !== newPhotoIndex));
        }

        setPhotosPreviews(prevPreviews => {
            const updatedPreviews = prevPreviews.filter((_, i) => i !== index);
            let newIndex = Math.min(currentPhotoIndex, updatedPreviews.length - 1);
            if (newIndex < 0) newIndex = 0;

            setCurrentPhotoIndex(newIndex);
            setPhotoPreview(updatedPreviews.length > 0 ? updatedPreviews[newIndex] : null);
            return updatedPreviews;
        });

        setPhotoCaptions(prevCaptions => prevCaptions.filter((_, i) => i !== index));

        // Ensure primaryPhotoIndex remains valid
        setPrimaryPhotoIndex(prev => {
            if (prev === index) {
                return Math.max(0, Math.min(photosPreviews.length - 1, index));
            } else if (index < prev) {
                return Math.max(0, prev - 1);
            }
            return prev;
        });
    }, [currentPhotoIndex, isUpdateMode, existingPhotosCount, existingPhotoIds, photosPreviews.length]);

    // Handle photo caption change
    const handleCaptionChange = useCallback((index, caption) => {
        setPhotoCaptions(prevCaptions => {
            const updatedCaptions = [...prevCaptions];
            updatedCaptions[index] = caption;
            return updatedCaptions;
        });
    }, []);

    const reorderArray = (arr, fromIndex, toIndex) => {
        const next = [...arr];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
    };

    const reorderIndex = (index, fromIndex, toIndex) => {
        if (index === fromIndex) return toIndex;
        if (fromIndex < toIndex && index > fromIndex && index <= toIndex) return index - 1;
        if (fromIndex > toIndex && index >= toIndex && index < fromIndex) return index + 1;
        return index;
    };

    const handlePhotoDragStart = useCallback((e, index) => {
        try {
            if (e && e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(index));
            }
        } catch (err) {
        }
        setDraggedPhotoIndex(index);
    }, []);

    const handlePhotoDragOver = useCallback((e) => {
        e.preventDefault();
    }, []);

    const handlePhotoDrop = useCallback((dropIndex) => {
        if (draggedPhotoIndex === null || draggedPhotoIndex === undefined) return;
        const fromIndex = draggedPhotoIndex;
        const toIndex = dropIndex;
        if (fromIndex === toIndex) return;

        const fromIsExisting = isUpdateMode && fromIndex < existingPhotosCount;
        const toIsExisting = isUpdateMode && toIndex < existingPhotosCount;

        if (isUpdateMode && fromIsExisting !== toIsExisting) {
            setDraggedPhotoIndex(null);
            return;
        }

        setPhotosPreviews(prev => reorderArray(prev, fromIndex, toIndex));
        setPhotoCaptions(prev => reorderArray(prev, fromIndex, toIndex));

        if (isUpdateMode && fromIsExisting && toIsExisting) {
            setExistingPhotoIds(prev => reorderArray(prev, fromIndex, toIndex));
        }

        if (!isUpdateMode || (!fromIsExisting && !toIsExisting)) {
            const fromNew = isUpdateMode ? fromIndex - existingPhotosCount : fromIndex;
            const toNew = isUpdateMode ? toIndex - existingPhotosCount : toIndex;
            setPhotos(prev => reorderArray(prev, fromNew, toNew));
        }

        setCurrentPhotoIndex(prev => reorderIndex(prev, fromIndex, toIndex));
        setPrimaryPhotoIndex(prev => reorderIndex(prev, fromIndex, toIndex));
        setDraggedPhotoIndex(null);
    }, [draggedPhotoIndex, isUpdateMode, existingPhotosCount]);

    const handleMovePhoto = useCallback((index, direction) => {
        const fromIndex = index;
        const toIndex = direction === 'left' ? index - 1 : index + 1;
        if (toIndex < 0 || toIndex >= photosPreviews.length) return;

        const fromIsExisting = isUpdateMode && fromIndex < existingPhotosCount;
        const toIsExisting = isUpdateMode && toIndex < existingPhotosCount;
        if (isUpdateMode && fromIsExisting !== toIsExisting) return;

        setPhotosPreviews(prev => reorderArray(prev, fromIndex, toIndex));
        setPhotoCaptions(prev => reorderArray(prev, fromIndex, toIndex));

        if (isUpdateMode && fromIsExisting && toIsExisting) {
            setExistingPhotoIds(prev => reorderArray(prev, fromIndex, toIndex));
        }

        if (!isUpdateMode || (!fromIsExisting && !toIsExisting)) {
            const fromNew = isUpdateMode ? fromIndex - existingPhotosCount : fromIndex;
            const toNew = isUpdateMode ? toIndex - existingPhotosCount : toIndex;
            setPhotos(prev => reorderArray(prev, fromNew, toIndex));
        }

        setCurrentPhotoIndex(prev => reorderIndex(prev, fromIndex, toIndex));
        setPrimaryPhotoIndex(prev => reorderIndex(prev, fromIndex, toIndex));
    }, [existingPhotosCount, isUpdateMode, photosPreviews.length]);

    // Handle form submission
    const handleSubmit = useCallback(async (withTranslationCheck = false) => {
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (!userProfile || !userProfile.id) {
            setErrors(prev => ({
                ...prev,
                owner: t('pro_salon.error.profile_not_found')
            }));
            return;
        }

        if (!formData.name || formData.name.trim() === '') {
            setErrors(prev => ({
                ...prev,
                name: t('pro_salon.validation.name_required')
            }));
            return;
        }

        try {
            const apiFormData = new FormData();

            // Add basic salon info
            apiFormData.append('name', formData.name.trim());
            if (formData.description) apiFormData.append('description', formData.description);
            if (formData.phone) apiFormData.append('phone', formData.phone);
            if (formData.email) apiFormData.append('email', formData.email);

            // Set owner: if superadmin uses selectedOwnerId, otherwise use userProfile.id
            const ownerId = isSuperAdmin ? selectedOwnerId : userProfile.id;
            if (ownerId) {
                apiFormData.append('owner', ownerId);
            }

            if (formData.opening_hours) {
                // Transform opening_hours to French format for backend compatibility
                const dayNameMap = {
                    // Arabic to French
                    'الإثنين': 'lundi',
                    'الثلاثاء': 'mardi',
                    'الأربعاء': 'mercredi',
                    'الخميس': 'jeudi',
                    'الجمعة': 'vendredi',
                    'السبت': 'samedi',
                    'الأحد': 'dimanche',
                    // English to French
                    'Monday': 'lundi',
                    'Tuesday': 'mardi',
                    'Wednesday': 'mercredi',
                    'Thursday': 'jeudi',
                    'Friday': 'vendredi',
                    'Saturday': 'samedi',
                    'Sunday': 'dimanche'
                };
                const closedValueMap = {
                    'مغلق': 'fermé',
                    'Closed': 'fermé',
                    'closed': 'fermé',
                    'Fermé': 'fermé',
                    'fermé': 'fermé'
                };

                const transformedOpeningHours = {};
                for (const [day, hours] of Object.entries(formData.opening_hours)) {
                    const frenchDay = dayNameMap[day] || day;
                    // Normalize to lowercase to avoid duplicates
                    const normalizedDay = frenchDay.toLowerCase();
                    
                    // Only add if it's a valid day and not already processed
                    if (['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].includes(normalizedDay)) {
                        transformedOpeningHours[normalizedDay] = {
                            ouverture: closedValueMap[hours.ouverture] || hours.ouverture,
                            fermeture: closedValueMap[hours.fermeture] || hours.fermeture
                        };
                    }
                }
                apiFormData.append('opening_hours', JSON.stringify(transformedOpeningHours));
            }
            apiFormData.append(
                'appointment_proposal_default_expiry_hours',
                formData.appointment_proposal_default_expiry_hours || 24
            );
            if (formData.location_data) {
                apiFormData.append('location_data', JSON.stringify(formData.location_data));
            }

            // Ensure primaryPhotoIndex is valid
            const validPrimaryIndex = Math.min(Math.max(0, primaryPhotoIndex), photosPreviews.length - 1);

            // Handle photos
            if (isUpdateMode) {
                if (photosToDelete.length > 0) {
                    apiFormData.append('photos_to_delete', JSON.stringify(photosToDelete));
                }

                if (photos.length > 0) {
                    const files = [...photos];
                    if (validPrimaryIndex >= existingPhotosCount) {
                        const primaryNewIdx = validPrimaryIndex - existingPhotosCount;
                        if (primaryNewIdx >= 0 && primaryNewIdx < files.length) {
                            const [primaryFile] = files.splice(primaryNewIdx, 1);
                            files.unshift(primaryFile);
                            apiFormData.append('is_primary', 'true');
                        }
                    }
                    files.forEach((photo) => {
                        apiFormData.append('photos[]', photo);
                    });

                    const newPhotoCaptions = photos.map((_, index) => photoCaptions[existingPhotosCount + index] || '');
                    if (newPhotoCaptions.length > 0) {
                        apiFormData.append('photo_captions', JSON.stringify(newPhotoCaptions));
                    }
                }

                if (validPrimaryIndex < existingPhotosCount) {
                    let primaryExistingId = existingPhotoIds[validPrimaryIndex];
                    if ((primaryExistingId === undefined || primaryExistingId === null) && salonToUpdate?.photos) {
                        if (Array.isArray(salonToUpdate.photos)) {
                            const p = salonToUpdate.photos[validPrimaryIndex];
                            primaryExistingId = p && (p.id || p.photo_id || p.pk);
                        } else if (typeof salonToUpdate.photos === 'object') {
                            const keys = Object.keys(salonToUpdate.photos);
                            primaryExistingId = parseInt(keys[validPrimaryIndex], 10) || keys[validPrimaryIndex];
                        }
                    }
                    if (primaryExistingId !== undefined && primaryExistingId !== null) {
                        apiFormData.append('set_primary_image', String(primaryExistingId));
                    }
                }

                if (photosToDelete.length === existingPhotosCount && photos.length > 0) {
                    apiFormData.append('replace_all_photos', 'true');
                }

                if (existingPhotoIds.length > 0) {
                    const ids = existingPhotoIds.filter((v) => v !== undefined && v !== null);
                    if (ids.length > 0) {
                        apiFormData.append('photo_order', JSON.stringify(ids));
                    }
                }
            } else {
                if (photos.length === 1) {
                    apiFormData.append('photo', photos[0]);
                    if (photoCaptions[validPrimaryIndex]) apiFormData.append('photo_caption', photoCaptions[validPrimaryIndex]);
                    apiFormData.append('is_primary', 'true');
                } else if (photos.length > 1) {
                    const files = [...photos];
                    const primaryNewIdx = validPrimaryIndex;
                    if (primaryNewIdx >= 0 && primaryNewIdx < files.length) {
                        const [primaryFile] = files.splice(primaryNewIdx, 1);
                        files.unshift(primaryFile);
                    }
                    files.forEach((photo) => {
                        apiFormData.append('photos[]', photo);
                    });
                    if (photoCaptions.length > 0) {
                        apiFormData.append('photo_captions', JSON.stringify(photoCaptions));
                    }
                    apiFormData.append('is_primary', 'true');
                }
            }

            console.log('[GLOW][AddSalon] preparing request', {
                isUpdateMode,
                selectedPhotosCount: photos?.length || 0,
                previewsCount: photosPreviews?.length || 0,
                existingPhotosCount,
                photosToDeleteCount: photosToDelete?.length || 0,
            });
            try {
                const entries = [];
                apiFormData.forEach((value, key) => {
                    if (value instanceof File) {
                        entries.push({
                            key,
                            name: value.name,
                            size: value.size,
                            type: value.type,
                        });
                    } else {
                        entries.push({ key, value: String(value).slice(0, 200) });
                    }
                });
                console.log('[GLOW][AddSalon] FormData entries', entries);
            } catch (e) {
                console.log('[GLOW][AddSalon] FormData inspection failed', e?.message || e);
            }

            let result;
            if (isUpdateMode && salonToUpdate) {
                result = await updateSalon({
                    salonId: salonToUpdate.salon_id,
                    body: apiFormData
                }).unwrap();

                // Show success message for update
                setSnackbarMessage(t('pro_salon.success.updated'));
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                // Close dialog and notify parent after showing toast
                setTimeout(() => {
                    if (onSuccess) {
                        onSuccess(result);
                    }
                    setShowEditForm(false);
                    onClose(); // Fermer le dialogue
                }, 1500); // Delay to show success message

                setPhotosToDelete([]);
                if (result?.photos) {
                    const existingPreviews = [];
                    const existingCaptions = [];
                    const existingIds = [];
                    let photoCount = 0;
                    let newPrimaryIndex = 0;

                    if (Array.isArray(result.photos)) {
                        result.photos.forEach((photo, idx) => {
                            if (photo.url) {
                                existingPreviews.push(photo.url);
                                existingCaptions.push(photo.caption || '');
                                existingIds.push(photo.id || photo.photo_id || photo.pk || null);
                                if (photo.is_primary) newPrimaryIndex = idx;
                                photoCount++;
                            }
                        });
                    } else if (typeof result.photos === 'object') {
                        const entries = Object.entries(result.photos);
                        entries.forEach(([key, photoUrl]) => {
                            if (photoUrl && typeof photoUrl === 'string') {
                                existingPreviews.push(photoUrl);
                                existingCaptions.push('');
                                existingIds.push(parseInt(key, 10) || key);
                                photoCount++;
                            }
                        });
                    }

                    setPhotosPreviews(existingPreviews);
                    setPhotoCaptions(existingCaptions);
                    setExistingPhotosCount(photoCount);
                    setExistingPhotoIds(existingIds);
                    setPhotoPreview(existingPreviews.length > 0 ? existingPreviews[newPrimaryIndex] : null);
                    setCurrentPhotoIndex(newPrimaryIndex);
                    setPrimaryPhotoIndex(newPrimaryIndex);
                    setPhotos([]);
                }
            } else {
                result = await createSalon({ body: apiFormData }).unwrap();

                console.log('Salon created - result:', result);
                const newSalonId = result?.salon_id || result?.id;
                console.log('Salon ID:', newSalonId);

                if (withTranslationCheck) {
                    // Open translation validation modal and poll for translations
                    setCreatedSalonId(newSalonId);
                    setPendingOnSuccess(() => () => onSuccess && onSuccess(result));
                    setTranslationModalOpen(true);
                    setTranslationModalLoading(true);
                    setPendingTranslations(null);

                    // Poll until translations are populated (signal runs async)
                    let attempts = 0;
                    const maxAttempts = 20;
                    pollIntervalRef.current = setInterval(async () => {
                        attempts++;
                        try {
                            const response = await fetch(
                                `/api/salons/${newSalonId}/`,
                                { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}` } }
                            );
                            if (response.ok) {
                                const data = await response.json();
                                const hasTranslations = data.name_translations && Object.keys(data.name_translations).length > 0;
                                if (hasTranslations) {
                                    clearInterval(pollIntervalRef.current);
                                    setPendingTranslations({
                                        name: data.name_translations,
                                        description: data.description_translations || {},
                                    });
                                    setTranslationModalLoading(false);
                                    return;
                                }
                            }
                        } catch (e) {
                            console.warn('[translations] poll error', e);
                        }
                        if (attempts >= maxAttempts) {
                            clearInterval(pollIntervalRef.current);
                            setTranslationModalLoading(false);
                        }
                    }, 1500);
                } else {
                    // Save without translation check
                    handleSuccess(t('pro_salon.success.created'));
                    if (onSuccess) onSuccess(result);
                    setTimeout(() => { onClose(); }, 1000);
                }

                return; // Early return to avoid the common cleanup below
            }
            if (!isUpdateMode) {
                setFormData(initializeFormData());
                setLocationTouched(false);
                setPhotos([]);
                setPhotosPreviews([]);
                setPhotoCaptions([]);
                setPhotoPreview(null);
                setCurrentPhotoIndex(0);
                setPrimaryPhotoIndex(0);
                setPhotosToDelete([]);
                setExistingPhotosCount(0);
                setExistingPhotoIds([]);
            }
            setErrors({});
        } catch (error) {
            console.error(`Failed to ${isUpdateMode ? 'update' : 'create'} salon:`, error);
            let apiErrors = {};
            let snackbarText = '';
            const openingHoursHelpText = t('pro_salon.error.opening_hours_invalid');
            const isOpeningHoursIdenticalError = (message) => {
                const normalized = String(message || '').toLowerCase();
                return normalized.includes(t('pro_salon.error.opening_hours_identical'));
            };
            const backendErrorPayload = error?.data || error?.response?.data || error || {};
            const rawErrorPayload = JSON.stringify(backendErrorPayload).toLowerCase();
            const hasOpeningHoursIdenticalError = rawErrorPayload.includes(t('pro_salon.error.opening_hours_identical'));

            if (hasOpeningHoursIdenticalError) {
                snackbarText = openingHoursHelpText;
                setErrors({ opening_hours: openingHoursHelpText });
                setSnackbarMessage(openingHoursHelpText);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                scrollToOpeningHoursError();
                if (onSuccess) onSuccess(null, error);
                return;
            }

            if (backendErrorPayload) {
                if (typeof backendErrorPayload === 'string') {
                    snackbarText = backendErrorPayload;
                    apiErrors.general = backendErrorPayload;
                } else if (backendErrorPayload.detail) {
                    if (typeof backendErrorPayload.detail === 'string') {
                        if (!snackbarText) {
                            snackbarText = backendErrorPayload.detail;
                        }
                        apiErrors.general = backendErrorPayload.detail;
                    } else if (typeof backendErrorPayload.detail === 'object') {
                        Object.entries(backendErrorPayload.detail).forEach(([key, value]) => {
                            apiErrors[key] = Array.isArray(value) ? value.join(', ') : value;
                        });
                        if (!snackbarText) {
                            const firstErrorKey = Object.keys(apiErrors)[0];
                            snackbarText = `${firstErrorKey}: ${apiErrors[firstErrorKey]}`;
                        }
                    }
                } else if (typeof backendErrorPayload === 'object') {
                    Object.entries(backendErrorPayload).forEach(([key, value]) => {
                        apiErrors[key] = Array.isArray(value) ? value.join(', ') : value;
                        if (key === 'name' && Array.isArray(value) && value.includes(t('pro_salon.error.duplicate_name'))) {
                            snackbarText = t('pro_salon.error.duplicate_name');
                        }

                        if (key === 'opening_hours' && Array.isArray(value) && value.some(isOpeningHoursIdenticalError)) {
                            snackbarText = openingHoursHelpText;
                        }
                    });
                    if (!snackbarText && Object.keys(apiErrors).length > 0) {
                        const firstKey = Object.keys(apiErrors)[0];
                        snackbarText = `${firstKey}: ${apiErrors[firstKey]}`;
                    }
                }
                setErrors(apiErrors);
                if (snackbarText) {
                    setSnackbarMessage(snackbarText);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                } else {
                    const errorMessage = isUpdateMode ? t('pro_salon.error.update_failed') : t('pro_salon.error.create_failed');
                    setSnackbarMessage(errorMessage);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            } else {
                const errorMessage = isUpdateMode ? t('pro_salon.error.update_failed') : t('pro_salon.error.create_failed');
                setErrors({ general: errorMessage });
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
            if (onSuccess) onSuccess(null, error);
        }
    }, [validateForm, formData, userProfile, photos, photoCaptions, photosToDelete, existingPhotosCount, isUpdateMode, salonToUpdate, createSalon, updateSalon, onSuccess, initializeFormData, primaryPhotoIndex, existingPhotoIds, photosPreviews.length, isSuperAdmin, selectedOwnerId, scrollToOpeningHoursError]);

    // Handle dialog close
    const handleDialogClose = useCallback(() => {
        if (!isUpdateMode) {
            setFormData(initializeFormData());
            setLocationTouched(false);
            setPhotos([]);
            setPhotosPreviews([]);
            setPhotoCaptions([]);
            setPhotoPreview(null);
            setCurrentPhotoIndex(0);
            setPrimaryPhotoIndex(0);
            setPhotosToDelete([]);
            setExistingPhotosCount(0);
            setExistingPhotoIds([]);
            setSalonCreated(false);
            setCreatedSalonData(null);
            setSelectedOwnerId('');
        }
        setShowEditForm(false); // Reset showEditForm pour permettre la fermeture
        setErrors({});
        onClose();
    }, [isUpdateMode, initializeFormData, onClose]);

    // Close Snackbar
    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    // Handle translation modal validate
    const handleTranslationValidate = useCallback(async (editedTranslations) => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);
        if (createdSalonId) {
            try {
                const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                await fetch(`/api/salons/${createdSalonId}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        name_translations: editedTranslations.name,
                        description_translations: editedTranslations.description,
                        translations_auto: false,
                    }),
                });
            } catch (e) {
                console.warn('[translations] patch error', e);
            }
        }
        handleSuccess(t('pro_salon.success.created'));
        if (pendingOnSuccess) pendingOnSuccess();
        setTimeout(() => { onClose(); }, 1000);
    }, [createdSalonId, pendingOnSuccess, onClose, t]);

    // Handle translation modal skip (close without patching)
    const handleTranslationSkip = useCallback(() => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);
        handleSuccess(t('pro_salon.success.created'));
        if (pendingOnSuccess) pendingOnSuccess();
        setTimeout(() => { onClose(); }, 1000);
    }, [pendingOnSuccess, onClose, t]);

    // Copy hours to all selected days
    const handleCopyHoursToAll = useCallback(() => {
        const selectedDay = weekdays.find(day => selectedDays.includes(day.id));
        if (!selectedDay) return;
        const sourceDayName = selectedDay.fullName;
        const sourceHours = formData.opening_hours[sourceDayName];
        if (!sourceHours || sourceHours.ouverture === t('pro_dashboard.salon.closed')) return;

        setFormData(prev => ({
            ...prev,
            opening_hours: {
                ...prev.opening_hours,
                ...Object.fromEntries(
                    selectedDays
                        .map(id => weekdays.find(d => d.id === id)?.fullName)
                        .filter(dayName => dayName && dayName !== sourceDayName)
                        .map(dayName => [dayName, { ...sourceHours }])
                )
            }
        }));
    }, [weekdays, selectedDays, formData.opening_hours, t, i18n.language]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        if (showEditForm && isUpdateMode) {
            // In UPDATE mode, close the dialog directly
            handleDialogClose();
        } else if (showEditForm) {
            // In creation mode, return to creation view
            setShowEditForm(false);
        } else if (salonCreated || isUpdateMode) {
            // Reset to initial state or close dialog
            if (salonCreated) {
                setSalonCreated(false);
                setCreatedSalonData(null);
            }
            handleDialogClose();
        } else {
            // For initial creation form, close the dialog
            handleDialogClose();
        }
    }, [showEditForm, salonCreated, isUpdateMode, handleDialogClose]);

    // Render fixed header with salon info and actions
    const renderFixedHeader = () => {
        const salonData = createdSalonData || salonToUpdate || {};
        const isEditing = showEditForm || isUpdateMode;
        const showSalonInfo = salonCreated && !showEditForm;
        
        // Debug logging
        console.log('[GLOW][AddSalon] renderFixedHeader debug:', {
            salonData,
            createdSalonData,
            salonToUpdate,
            showSalonInfo,
            isEditing
        });

        return (
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: theme.colors.primary[100],
                    color: theme.colors.grey[900],
                    zIndex: 1300,
                    top: 0
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexDirection: 'row',
                        gap: isMobile ? 1.5 : 0
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, width: isMobile ? '100%' : 'auto', mt: isMobile ? 1 : 1.5, mb: isMobile ? 1 : 1.5 }}>
                        <IconButton
                            edge="start"
                            onClick={handleBack}
                            aria-label={t('pro_dashboard.common.back')}
                            sx={{
                                color: theme.colors.grey[900],
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            {showEditForm || salonCreated || isUpdateMode ? <ArrowBackIcon /> : <CloseIcon />}
                        </IconButton>

                        {showSalonInfo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <StorefrontIcon sx={{ fontSize: '1.5rem', color: theme.colors.grey[900] }} />
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: theme.colors.grey[900],
                                            fontWeight: 600,
                                            fontSize: isMobile ? '1rem' : '1.25rem'
                                        }}
                                    >
                                        {getLocalizedName(salonData) || t('pro_dashboard.salon.salon_no_name')}
                                    </Typography>
                                    {salonData.formatted_address && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: theme.colors.primary[900],
                                                fontSize: isMobile ? '0.7rem' : '0.8rem'
                                            }}
                                        >
                                            {salonData.formatted_address}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {!showSalonInfo && (
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.colors.grey[900],
                                        fontWeight: 600,
                                        fontSize: isMobile ? '1rem' : '1.25rem'
                                    }}
                                >
                                    {isUpdateMode ? t('pro_dashboard.salon.edit_salon') : t('pro_dashboard.salon.add_new_salon')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.colors.primary[900],
                                        fontSize: isMobile ? '0.7rem' : '0.8rem'
                                    }}
                                >
                                    {isUpdateMode ? t('pro_dashboard.salon.edit_salon_desc') : t('pro_dashboard.salon.create_salon_desc')}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            gap: isMobile ? 0.5 : 1,
                            flexDirection: isMobile ? 'column' : 'row',
                            flexShrink: 0,
                            mt: isMobile ? 1 : 1.5,
                            mb: isMobile ? 1 : 1.5
                        }}
                    >
                        {isMobile ? (
                            <>
                                {(isEditing || (!salonCreated && !isUpdateMode)) && (
                                    <>
                                        <Button
                                            variant="contained"
                                            startIcon={isCreating || isUpdating ? <CircularProgress size={16} /> : <SaveIcon />}
                                            onClick={() => handleSubmit(false)}
                                            disabled={isCreating || isUpdating || profileLoading}
                                            sx={{
                                                backgroundColor: theme.colors.primary[200],
                                                color: theme.colors.primary[900],
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                padding: '6px 14px',
                                                minWidth: 140,
                                                justifyContent: 'center',
                                                '&:hover': { backgroundColor: theme.colors.secondary[400] },
                                                '&:disabled': { backgroundColor: theme.colors.secondary[300], color: theme.colors.secondary[500] }
                                            }}
                                        >
                                            {isCreating || isUpdating ? t('pro_dashboard.salon.saving') : (isUpdateMode ? t('pro_dashboard.salon.update') : t('pro_dashboard.common.save'))}
                                        </Button>
                                        {!isUpdateMode && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<TranslateIcon fontSize="small" />}
                                                onClick={() => handleSubmit(true)}
                                                disabled={isCreating || isUpdating || profileLoading}
                                                sx={{
                                                    borderColor: theme.colors.primary[400],
                                                    color: theme.colors.primary[700],
                                                    fontSize: '0.7rem',
                                                    padding: '6px 10px',
                                                    minWidth: 140,
                                                    justifyContent: 'center',
                                                    '&:hover': { borderColor: theme.colors.primary[600], backgroundColor: theme.colors.primary[50] }
                                                }}
                                            >
                                                {t('pro_salon.translations.save_and_check', 'Enr. & Traduction')}
                                            </Button>
                                        )}
                                    </>
                                )}
                                {isEditing && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => (isUpdateMode ? handleDialogClose() : setShowEditForm(false))}
                                        sx={{
                                            color: theme.colors.primary[600],
                                            borderColor: theme.colors.primary[500],
                                            fontSize: '0.7rem',
                                            padding: '4px 8px',
                                            minWidth: 140,
                                            justifyContent: 'center',
                                            '&:hover': { borderColor: theme.colors.primary[300], backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                        }}
                                    >
                                        {t('pro_dashboard.common.cancel')}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                {isEditing && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => (isUpdateMode ? handleDialogClose() : setShowEditForm(false))}
                                        sx={{
                                            color: theme.colors.primary[600],
                                            borderColor: theme.colors.primary[500],
                                            fontSize: '0.875rem',
                                            padding: '6px 12px',
                                            '&:hover': { borderColor: theme.colors.primary[300], backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                        }}
                                    >
                                        {t('pro_dashboard.common.cancel')}
                                    </Button>
                                )}
                                {(isEditing || (!salonCreated && !isUpdateMode)) && (
                                    <>
                                        <Button
                                            variant="contained"
                                            startIcon={isCreating || isUpdating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={() => handleSubmit(false)}
                                            disabled={isCreating || isUpdating || profileLoading}
                                            sx={{
                                                backgroundColor: theme.colors.primary[200],
                                                color: theme.colors.primary[900],
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                padding: '6px 16px',
                                                '&:hover': { backgroundColor: theme.colors.secondary[400] },
                                                '&:disabled': { backgroundColor: theme.colors.secondary[300], color: theme.colors.secondary[500] }
                                            }}
                                        >
                                            {isCreating || isUpdating ? t('pro_dashboard.salon.saving') : (isUpdateMode ? t('pro_dashboard.salon.update') : t('pro_dashboard.common.save'))}
                                        </Button>
                                        {!isUpdateMode && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<TranslateIcon fontSize="small" />}
                                                onClick={() => handleSubmit(true)}
                                                disabled={isCreating || isUpdating || profileLoading}
                                                sx={{
                                                    borderColor: theme.colors.primary[400],
                                                    color: theme.colors.primary[700],
                                                    fontSize: '0.875rem',
                                                    padding: '6px 14px',
                                                    '&:hover': { borderColor: theme.colors.primary[600], backgroundColor: theme.colors.primary[50] }
                                                }}
                                            >
                                                {t('pro_salon.translations.save_and_check', 'Enregistrer & Vérifier traduction')}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
        );
    };

    // Form content - show CreationEditView only after salon creation (not in update mode), otherwise show form
    const formContent = ((salonCreated && createdSalonData) && !showEditForm && !isUpdateMode) ? (
        <Box sx={{
            pt: 10,
            minHeight: '100vh'
        }}>
            <CreationEditView
                open={false}
                salon={createdSalonData || salonToUpdate}
                onEdit={() => {
                    setShowEditForm(true);
                }}
                isUpdateMode={isUpdateMode}
                salonToUpdate={salonToUpdate || createdSalonData}
                onSuccess={handleSuccess}
                onClose={handleDialogClose}
            />
        </Box>
    ) : (
        <Box sx={{
            p: isMobile ? 2 : 4,
            pt: 10,
            backgroundColor: '#f8f9fa',
            minHeight: '100vh'
        }}>
            <Box sx={{ maxWidth: isMobile ? '100%' : 800, mx: 'auto' }}>
                {errors.opening_hours && (
                    <Alert
                        ref={openingHoursErrorRef}
                        severity="error"
                        variant="filled"
                        sx={{ mb: 2, borderRadius: 1.5 }}
                    >
                        {errors.opening_hours}
                    </Alert>
                )}
                <Card sx={{
                    mb: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    border: 'none'
                }}>
                    <CardContent sx={{ p: isMobile ? 1 : 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                fontSize: isMobile ? '1.25rem' : '1.5rem'
                            }}
                        >
                            {t('pro_dashboard.salon.general_info')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#666',
                                mb: 3,
                                fontSize: '0.95rem'
                            }}
                        >
                            {t('pro_dashboard.salon.general_info_desc')}
                        </Typography>

                        <Grid container spacing={3}>
                            {isSuperAdmin && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" error={!!errors.owner}>
                                        <InputLabel id="owner-select-label">{t('pro_dashboard.salon.owner_label')} *</InputLabel>
                                        <Select
                                            labelId="owner-select-label"
                                            id="owner-select"
                                            value={selectedOwnerId}
                                            label={t('pro_dashboard.salon.owner_label') + ' *'}
                                            onChange={(e) => {
                                                // console.log("[GLOW][AddSalon] Select onChange:", e.target.value);
                                                setSelectedOwnerId(e.target.value);
                                            }}
                                            sx={{
                                                backgroundColor: '#fff',
                                                borderRadius: 1.5,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#e0e0e0',
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                                disablePortal: false,
                                                zIndex: 1400
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>{loadingOwners ? t('pro_dashboard.common.loading') : t('pro_dashboard.salon.select_owner')}</em>
                                            </MenuItem>

                                            {!loadingOwners && Array.isArray(owners) && owners.map((owner) => (
                                                <MenuItem key={owner.id} value={owner.id}>
                                                    {owner.first_name} {owner.last_name} ({owner.email})
                                                </MenuItem>
                                            ))}

                                            {!loadingOwners && Array.isArray(owners) && owners.length === 0 && (
                                                <MenuItem disabled value="">
                                                    {t('pro_dashboard.salon.no_owner_found')}
                                                </MenuItem>
                                            )}
                                        </Select>
                                        {errors.owner && <FormHelperText>{errors.owner}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    name="name"
                                    label={t('pro_dashboard.salon.salon_name') + ' *'}
                                    value={formData.name}
                                    onChange={handleChange}
                                    disabled={isCreating || isUpdating}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    placeholder={t('pro_dashboard.salon.salon_name_placeholder')}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 1.5,
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} >
                                <TextField
                                    name="phone"
                                    label={t('pro_dashboard.salon.phone')}
                                    value={formData.phone}
                                    onChange={(e) => {
                                        const { name, value } = e.target;
                                        if (value && !/^[+]?[0-9]*$/.test(value)) {
                                            setErrors(prev => ({
                                                ...prev,
                                                phone: t('pro_dashboard.salon.phone_invalid')
                                            }));
                                            return;
                                        }
                                        setFormData(prev => ({ ...prev, [name]: value }));
                                        if (errors.phone) {
                                            setErrors(prev => ({ ...prev, phone: null }));
                                        }
                                    }}
                                    disabled={isCreating || isUpdating}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    placeholder="05 55 55 55 55"
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    inputProps={{ pattern: '[+]?[0-9]{1,15}', inputMode: 'tel' }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 1.5,
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={4}>
                                <TextField
                                    name="email"
                                    label={t('pro_dashboard.salon.email')}
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => {
                                        const { name, value } = e.target;
                                        setFormData(prev => ({ ...prev, [name]: value }));
                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                        if (value && !emailRegex.test(value)) {
                                            setErrors(prev => ({ ...prev, email: t('pro_dashboard.salon.email_invalid') }));
                                        } else if (errors.email) {
                                            setErrors(prev => ({ ...prev, email: null }));
                                        }
                                    }}
                                    disabled={isCreating || isUpdating}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    placeholder="contact@salon.com"
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    inputProps={{ inputMode: 'email' }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 1.5,
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    name="description"
                                    label={t('pro_dashboard.salon.description')}
                                    value={formData.description}
                                    onChange={handleChange}
                                    disabled={isCreating || isUpdating}
                                    placeholder={t('pro_dashboard.salon.description_placeholder')}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 1.5,
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Section 2: Localisation */}
                <Card sx={{
                    mb: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    border: 'none'
                }}>
                    <CardContent sx={{ p: isMobile ? 3 : 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                fontSize: isMobile ? '1.25rem' : '1.5rem'
                            }}
                        >
                            {t('pro_dashboard.salon.location')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#666',
                                mb: 3,
                                fontSize: '0.95rem'
                            }}
                        >
                            {t('pro_dashboard.salon.location_desc')}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                color: '#333',
                                mb: 2,
                                fontSize: '0.9rem'
                            }}
                        >
                            {t('pro_dashboard.salon.complete_address')}
                        </Typography>

                        <SalonAddressPicker
                            value={formData.location_data || formData.location_preview}
                            onChange={handleAddressCandidateChange}
                            disabled={isCreating || isUpdating}
                            requireConfirmation={!isUpdateMode || locationTouched}
                        />
                        {errors.location_data && (
                            <FormHelperText error sx={{ mt: 1 }}>
                                {errors.location_data}
                            </FormHelperText>
                        )}
                    </CardContent>
                </Card>

                {/* Section 3: Photos du salon */}
                <Card
                    ref={photoSectionRef}
                    data-testid="salon-photo-editor-section"
                    sx={{
                        mb: 3,
                        scrollMarginTop: { xs: 80, md: 88 },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 2,
                        border: 'none'
                    }}
                >
                    <CardContent sx={{ p: isMobile ? 3 : 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                fontSize: isMobile ? '1.25rem' : '1.5rem'
                            }}
                        >
                            {t('pro_dashboard.salon.salon_photos')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#666',
                                mb: 3,
                                fontSize: '0.95rem'
                            }}
                        >
                            {t('pro_dashboard.salon.salon_photos_desc')}
                        </Typography>

                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="salon-photo-upload"
                            type="file"
                            onChange={handlePhotoUpload}
                            ref={fileInputRef}
                            disabled={isCreating || isUpdating}
                            multiple
                        />

                        <Grid container spacing={2}>
                            {/* Add Photo Button */}
                            <Grid item xs={6} sm={4} md={3}>
                                <label htmlFor="salon-photo-upload">
                                    <Box
                                        sx={{
                                            border: '2px dashed #e0e0e0',
                                            borderRadius: 2,
                                            height: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: '#fafafa',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: theme.colors.primary[500],
                                                backgroundColor: '#f5f5f5'
                                            }
                                        }}
                                        component="div"
                                    >
                                        <AddIcon sx={{ fontSize: 32, color: '#999', mb: 1 }} />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                textAlign: 'center',
                                                fontSize: '0.85rem',
                                                fontWeight: 500
                                            }}
                                        >
                                            {t('pro_dashboard.salon.add_photo')}
                                        </Typography>
                                    </Box>
                                </label>
                            </Grid>

                            {/* Existing Photos */}
                            {photosPreviews.map((preview, index) => (
                                <Grid item xs={6} sm={4} md={3} key={`photo-${isUpdateMode && index < existingPhotosCount ? existingPhotoIds[index] : preview}`}>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            height: 120,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: currentPhotoIndex === index ? `2px solid ${theme.colors.primary[500]}` : '1px solid #e0e0e0'
                                        }}
                                        draggable={!isCreating && !isUpdating}
                                        onDragStart={(e) => handlePhotoDragStart(e, index)}
                                        onDragOver={handlePhotoDragOver}
                                        onDrop={() => handlePhotoDrop(index)}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                left: 4,
                                                display: 'flex',
                                                gap: '4px',
                                                zIndex: 2,
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                    padding: '4px',
                                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
                                                }}
                                                onClick={() => handleMovePhoto(index, 'left')}
                                                disabled={isCreating || isUpdating || index === 0 || (isUpdateMode && index === existingPhotosCount)}
                                            >
                                                <ArrowLeftIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                    padding: '4px',
                                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
                                                }}
                                                onClick={() => handleMovePhoto(index, 'right')}
                                                disabled={isCreating || isUpdating || index === photosPreviews.length - 1 || (isUpdateMode && index === existingPhotosCount - 1)}
                                            >
                                                <ArrowRightIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <img
                                            src={preview}
                                            alt={`Photo ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleSelectPhoto(index)}
                                        />

                                        {/* Primary Photo Star */}
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 4,
                                                right: 4,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                padding: '4px',
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
                                            }}
                                            onClick={() => setPrimaryPhotoIndex(index)}
                                            disabled={isCreating || isUpdating}
                                        >
                                            {index === primaryPhotoIndex ? (
                                                <StarIcon fontSize="small" color="warning" />
                                            ) : (
                                                <StarBorderIcon fontSize="small" />
                                            )}
                                        </IconButton>

                                        {/* Delete Button */}
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                padding: '4px',
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
                                            }}
                                            onClick={() => handleRemovePhoto(index)}
                                            disabled={isCreating || isUpdating}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>

                                        {/* Primary Photo Badge */}
                                        {index === primaryPhotoIndex && (
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                backgroundColor: 'rgba(25, 118, 210, 0.9)',
                                                color: 'white',
                                                fontSize: '10px',
                                                padding: '2px 6px',
                                                borderRadius: '0 4px 0 0',
                                                fontWeight: 500
                                            }}>
                                                Principale
                                            </Box>
                                        )}

                                        {/* Update Mode Badge */}
                                        {isUpdateMode && (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                backgroundColor: index < existingPhotosCount ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 152, 0, 0.9)',
                                                color: 'white',
                                                fontSize: '8px',
                                                padding: '2px 4px',
                                                borderRadius: '0 0 4px 0',
                                                fontWeight: 500
                                            }}>
                                                {index < existingPhotosCount ? 'Exist.' : 'Nouv.'}
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {photosPreviews.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CameraAltIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                    {t('pro_dashboard.salon.photos_added', { count: photosPreviews.length, s: photosPreviews.length > 1 ? 's' : '' })}
                                    {isUpdateMode && existingPhotosCount > 0 && (
                                        <span style={{ marginLeft: 8 }}>
                                            ({existingPhotosCount} {t('pro_dashboard.salon.existing')}{existingPhotosCount > 1 ? 's' : ''}, {photos.length} {t('pro_dashboard.salon.new')}{photos.length > 1 ? 's' : ''})
                                        </span>
                                    )}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddPhotoAlternateIcon />}
                                component="label"
                                htmlFor="salon-photo-upload"
                                disabled={isCreating || isUpdating}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#666',
                                    '&:hover': {
                                        borderColor: theme.colors.primary[500],
                                        backgroundColor: 'rgba(0,0,0,0.02)'
                                    }
                                }}
                            >
                                {t('pro_dashboard.salon.add_photos')}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{
                    mb: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    border: 'none'
                }}>
                    <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 2 }}>
                            {t('appointment_workflow.proposal_settings.title')}
                        </Typography>
                        <SalonProposalExpiryField
                            value={formData.appointment_proposal_default_expiry_hours}
                            onChange={(value) => setFormData(prev => ({
                                ...prev,
                                appointment_proposal_default_expiry_hours: value,
                            }))}
                            label={t('appointment_workflow.proposal_settings.expiry_label')}
                            helperText={t('appointment_workflow.proposal_settings.expiry_help')}
                            disabled={isCreating || isUpdating}
                        />
                    </CardContent>
                </Card>

                {/* Section 4: Heures d'ouverture */}
                <Card sx={{
                    mb: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    border: 'none'
                }}>
                    <CardContent sx={{ p: isMobile ? 4 : 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                fontSize: isMobile ? '1.25rem' : '1.5rem'
                            }}
                        >
                            {t('pro_dashboard.salon.opening_hours')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#666',
                                mb: 3,
                                fontSize: '0.95rem'
                            }}
                        >
                            {t('pro_dashboard.salon.working_hours_title')}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                color: '#333',
                                mb: 2,
                                fontSize: '0.9rem'
                            }}
                        >
                            {t('pro_dashboard.salon.working_days')}
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <ToggleButtonGroup
                                value={selectedDays}
                                onChange={handleDayChange}
                                aria-label={t('pro_dashboard.salon.working_days')}
                                size="small"
                                disabled={isCreating || isUpdating}
                                sx={{
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px !important',
                                        px: 2,
                                        py: 1,
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        color: '#666',
                                        backgroundColor: '#fff',
                                        '&.Mui-selected': {
                                            backgroundColor: '#1a1a1a',
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: '#333',
                                            }
                                        },
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                        }
                                    }
                                }}
                            >
                                {weekdays.map(day => (
                                    <ToggleButton
                                        key={day.id}
                                        value={day.id}
                                    >
                                        {day.name}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>

                        {formData.opening_hours && selectedDays.length > 0 && (
                            <Box>
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            textDecoration: 'underline',
                                            p: 0,
                                            minWidth: 'auto',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                textDecoration: 'underline'
                                            }
                                        }}
                                        disabled={isCreating || isUpdating}
                                        onClick={handleCopyHoursToAll}
                                    >
                                        {t('pro_dashboard.salon.copy_for_all')}
                                    </Button>
                                </Box>

                                {weekdays
                                    .filter(day => selectedDays.includes(day.id))
                                    .map(day => {
                                        const dayName = day.fullName;
                                        const hours = formData.opening_hours[dayName] || { ouverture: t('pro_dashboard.salon.closed', { _tags: 'replace_all' }), fermeture: t('pro_dashboard.salon.closed', { _tags: 'replace_all' }) };

                                        return (
                                            <Box key={day.id} sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 2,
                                                        color: '#1a1a1a',
                                                        fontSize: '1rem'
                                                    }}
                                                >
                                                    {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                                                </Typography>

                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} sm={5}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#666',
                                                                mb: 1,
                                                                fontSize: '0.85rem'
                                                            }}
                                                        >
                                                            {t('pro_dashboard.salon.opening_label')}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TextField
                                                                type="text"
                                                                value={hours.ouverture !== t('pro_dashboard.salon.closed') ? hours.ouverture : ''}
                                                                disabled={isCreating || isUpdating}
                                                                size="small"
                                                                fullWidth
                                                                placeholder="09:00"
                                                                onChange={(e) => handleTimeInputChange(dayName, 'ouverture', e.target.value)}
                                                                onBlur={() => handleTimeInputBlur(dayName, 'ouverture')}
                                                                error={false}
                                                                InputProps={{}}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: '#fff',
                                                                        borderRadius: 1.5,
                                                                        '& fieldset': {
                                                                            borderColor: '#e0e0e0',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: '#ccc',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: theme.colors.primary[500],
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            <IconButton
                                                                onClick={(e) => handleTimePickerOpen(e, dayName, 'ouverture')}
                                                                disabled={isCreating || isUpdating}
                                                                size="small"
                                                                sx={{
                                                                    color: theme.colors.primary[500],
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(0,0,0,0.04)'
                                                                    }
                                                                }}
                                                            >
                                                                <AccessTimeIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={2} sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                                                        <Typography variant="body2" sx={{ color: '#999', fontWeight: 500 }}>
                                                            —
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={5}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#666',
                                                                mb: 1,
                                                                fontSize: '0.85rem'
                                                            }}
                                                        >
                                                            {t('pro_dashboard.salon.closing_label')}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TextField
                                                                type="text"
                                                                value={hours.fermeture !== t('pro_dashboard.salon.closed') ? hours.fermeture : ''}
                                                                disabled={isCreating || isUpdating}
                                                                size="small"
                                                                fullWidth
                                                                placeholder="18:00"
                                                                onChange={(e) => handleTimeInputChange(dayName, 'fermeture', e.target.value)}
                                                                onBlur={() => handleTimeInputBlur(dayName, 'fermeture')}
                                                                error={false}
                                                                InputProps={{}}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: '#fff',
                                                                        borderRadius: 1.5,
                                                                        '& fieldset': {
                                                                            borderColor: '#e0e0e0',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: '#ccc',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: theme.colors.primary[500],
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            <IconButton
                                                                onClick={(e) => handleTimePickerOpen(e, dayName, 'fermeture')}
                                                                disabled={isCreating || isUpdating}
                                                                size="small"
                                                                sx={{
                                                                    color: theme.colors.primary[500],
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(0,0,0,0.04)'
                                                                    }
                                                                }}
                                                            >
                                                                <AccessTimeIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    })}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );

    const memoizedContent = React.useMemo(() => formContent, [
        formData, selectedDays, isCreating, isUpdating, profileLoading,
        errors, photosPreviews, currentPhotoIndex, existingPhotosCount,
        photos.length, primaryPhotoIndex, salonCreated, createdSalonData, isUpdateMode, showEditForm,
        isSuperAdmin, selectedOwnerId, loadingOwners, owners, i18n.language
    ]);

    const [dialogClosed, setDialogClosed] = useState(false);

    const handleClose = () => {
        setDialogClosed(true);
        handleDialogClose();
    };

    // If salon is created and we're not in update mode, show the CreationEditView
    if (salonCreated && !isUpdateMode && createdSalonData) {
        return (
            <CreationEditView
                open={open}
                salon={createdSalonData}
                onClose={() => {
                    // Reset salon creation state and close dialog
                    setSalonCreated(false);
                    setCreatedSalonData(null);
                    handleDialogClose();
                }}
                onEdit={() => setShowEditForm(true)}
                isUpdateMode={false}
                salonToUpdate={null}
                onSuccess={(message) => {
                    if (onSuccess) onSuccess(message);
                }}
            />
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en'}>
            {/* Edit Form Dialog */}
            <FullScreenDialog
                open={showEditForm}
                triggerButton={<div style={{ display: 'none' }}></div>}
                title="" // Empty title since we have custom header
                subtitle="" // Empty subtitle since we have custom header
                content={
                    <>
                        {renderFixedHeader()}
                        {memoizedContent}
                    </>
                }
                existAction={false}
                actionTitle=""
                actionFunction={() => { }}
                onClose={() => setShowEditForm(false)}
                onExternalClose={() => setShowEditForm(false)}
                isLoading={false}
                actionType="button"
                disableAction={false}
                showCloseButton={true} // Enable close button
                showActionInToolbar={false}
                transitionDuration={0} // Disable animation
                sx={{ backgroundColor: theme.colors.secondary[50] }}
            />

            {/* Main Creation Form Dialog */}
            <FullScreenDialog
                open={open && !salonCreated}
                triggerButton={<div style={{ display: 'none' }}></div>}
                title="" // Empty title since we have custom header
                subtitle="" // Empty subtitle since we have custom header
                content={
                    <>
                        {renderFixedHeader()}
                        {memoizedContent}
                    </>
                }
                existAction={false} // Disable default action since we have custom header
                actionTitle=""
                actionFunction={() => { }}
                onClose={handleClose}
                onExternalClose={handleClose}
                isLoading={false} // Handle loading in custom header
                actionType="button"
                disableAction={false}
                showCloseButton={true} // Enable close button
                showActionInToolbar={false} // Hide default action since we have custom header
                transitionDuration={0} // Disable animation
                TransitionProps={{ onEntered: handlePhotoDialogEntered }}
                sx={{ backgroundColor: theme.colors.secondary[50] }}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ zIndex: 10000 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Time Picker Popover */}
            <Popover
                open={Boolean(timePickerAnchor)}
                anchorEl={timePickerAnchor}
                onClose={handleTimePickerClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0'
                    }
                }}
            >
                {activeTimePicker && (
                    <Box dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: '#666' }}>
                            {t('pro_dashboard.salon.select_time', { type: activeTimePicker.type === 'ouverture' ? t('pro_dashboard.salon.opening') : t('pro_dashboard.salon.closing') })}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', minWidth: 80, textAlign: 'center' }}>
                                {t('pro_dashboard.salon.hours')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', minWidth: 80, textAlign: 'center' }}>
                                {t('pro_dashboard.salon.minutes')}
                            </Typography>
                        </Box>
                        <MultiSectionDigitalClock
                            value={
                                (() => {
                                    const timeValue = formData.opening_hours[activeTimePicker.day]?.[activeTimePicker.type];
                                    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
                                    if (timeValue && timeRegex.test(timeValue)) {
                                        return dayjs(`2023-01-01T${timeValue}`);
                                    }
                                    return dayjs().startOf('day').hour(9);
                                })()
                            }
                            onChange={(newValue) => handleHoursChange(activeTimePicker.day, activeTimePicker.type, newValue)}
                            ampm={false}
                            views={['hours', 'minutes']}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            sx={{
                                maxHeight: 200,
                                overflow: 'auto',
                                '& .MuiMultiSectionDigitalClock-root': {
                                    maxWidth: 200,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-around',
                                    alignItems: 'center',
                                    gap: 2
                                },
                                '& .MuiMultiSectionDigitalClock-section': {
                                    flex: 1,
                                    minWidth: 80
                                },
                                '&[dir="rtl"] .MuiMultiSectionDigitalClock-section:first-of-type': {
                                    order: 2
                                },
                                '&[dir="rtl"] .MuiMultiSectionDigitalClock-section:last-of-type': {
                                    order: 1
                                }
                            }}
                        />
                    </Box>
                )}
            </Popover>

            <TranslationValidationModal
                open={translationModalOpen}
                onClose={handleTranslationSkip}
                onValidate={handleTranslationValidate}
                salonName={formData.name}
                salonDescription={formData.description}
                isLoading={translationModalLoading}
                translations={pendingTranslations}
            />
        </LocalizationProvider>
    );
};

export default AddSalon;
